import { Column, GridRow } from '@shared';
import { CurrencyPipe, DatePipe, PercentPipe } from '@angular/common';
import { IChangeset } from '@shared-data';
import { v4 as uuidv4 } from 'uuid';
import { IPricingItem } from './pricing-item.interface';

class KidMealChangeset implements IChangeset, IPricingItem {
  id: string;
  restaurantId: string;
  categoryId?: string;
  status?: string;

  @Column({
    field: 'restaurantName',
    title: 'Restaurant',
    order: 0,
    width: 80,
    sortable: true,
    visibleInDetailsView: false,
    visibleInQuickView: true,
    align: 'left',
  })
  restaurantName?: string;

  @Column({
    field: 'tierName',
    title: 'Tier',
    order: 0,
    width: 150,
    sortable: true,
    visibleInDetailsView: false,
    visibleInQuickView: true,
    align: 'left',
  })
  tierName?: string;

  plu?: string;

  @Column({
    field: 'comboParId',
    title: 'Brink ID',
    order: 2,
    width: 80,
    filterable: true,
    filterType: 'custom',
    type: 'number',
    sortable: true,
    visibleInQuickView: true,
    hidden: true,
  })
  comboParId?: string;

  @Column({
    field: 'comboName',
    title: 'Product',
    order: 1,
    width: 200,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  comboName: string;

  comboId: string;

  itemDescription?: string;

  @Column({
    field: 'itemPrice',
    title: 'Current Price (Without Toy)',
    order: 5,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 120,
    filterType: 'numeric',
    filterable: true,
    visibleInQuickView: true,
  })
  itemPrice: number;

  @Column({
    field: 'toyPrice',
    title: 'Toy Price',
    order: 5.1,
    type: 'number',
    pipeToken: CurrencyPipe,
    filterType: 'numeric',
    filterable: true,
    width: 80,
  })
  toyPrice?: number;

  @Column({
    field: 'totalPrice',
    title: 'Current Price (With Toy)',
    order: 5.2,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 120,
    visibleInQuickView: false,
  })
  get totalPrice(): number {
    return this.itemPrice + (this.toyPrice || 0);
  }

  @Column({
    field: 'deliveryPrice',
    title: 'Current Delivery Price',
    order: 10,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 100,
    filterType: 'numeric',
    filterable: true,
    visibleInQuickView: true,
  })
  deliveryPrice?: number | undefined;

  @Column({
    field: 'totalDeliveryPrice',
    title: 'Current Delivery Price (With Toy)',
    order: 10,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 100,
    visibleInQuickView: false,
  })
  get totalDeliveryPrice(): number | undefined {
    if (this.deliveryPrice === undefined || this.deliveryPrice === null) {
      return undefined;
    }
    return this.deliveryPrice + (this.toyPrice || 0);
  }

  @Column({
    field: 'newDeliveryPrice',
    title: 'New Delivery Price (Without Toy)',
    order: 11,
    editable: true,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 100,
    filterType: 'numeric',
    filterable: true,
    visibleInQuickView: true,
  })
  newDeliveryPrice?: number;

  @Column({
    field: 'newTotalDeliveryPrice',
    title: 'New Delivery Price (With Toy)',
    order: 11,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 100,
    visibleInQuickView: false,
  })
  get newTotalDeliveryPrice(): number | undefined {
    if (this.newDeliveryPrice === undefined || this.newDeliveryPrice === null) {
      return undefined;
    }
    return this.newDeliveryPrice + (this.toyPrice || 0);
  }

  @Column({
    field: 'newPrice',
    title: 'New Price (Without Toy)',
    order: 7,
    type: 'number',
    editable: true,
    pipeToken: CurrencyPipe,
    width: 100,
    filterType: 'numeric',
    filterable: true,
    visibleInQuickView: true,
  })
  newPrice?: number;

  @Column({
    field: 'newTotalPrice',
    title: 'New Price (With Toy)',
    order: 7,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 100,
    visibleInQuickView: false,
  })
  get newTotalPrice(): number | undefined {
    if (this.newPrice === undefined || this.newPrice === null) {
      return undefined;
    }
    return this.newPrice + (this.toyPrice || 0);
  }

  @Column({
    field: 'entreeName',
    title: 'Entree Name',
    order: 2,
    width: 200,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  entreeName?: string;

  entreeId?: string;

  @Column({
    field: 'entreePriceAlc',
    title: 'Entree Price ALC',
    order: 4.5,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 80,
    filterType: 'numeric',
    filterable: true,
    visibleInQuickView: true,
  })
  entreePriceAlc?: number;

  @Column({
    field: 'comboDiscount',
    title: 'Combo Discount',
    order: 6.2,
    type: 'number',
    width: 80,
    filterType: 'numeric',
    filterable: true,
    sortable: true,
    hidden: true,
    pipeToken: CurrencyPipe,
  })
  comboDiscount?: number;

  @Column({
    field: 'comboMarkup',
    title: 'Combo Markup',
    order: 6.1,
    type: 'number',
    width: 80,
    filterType: 'numeric',
    filterable: true,
    sortable: true,
    hidden: true,
    pipeToken: CurrencyPipe,
  })
  comboMarkup?: number;

  @Column({
    field: 'pricingScheduledDate',
    hidden: true,
    title: 'Price Change Scheduled Date',
    order: 9,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    editable: true,
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  pricingScheduledDate: Date | null;

  availabilityScheduledDate: Date | null;

  @Column({
    field: 'isActive',
    title: 'Is Active',
    order: 2,
    width: 60,
    filterable: true,
    filterType: 'boolean',
    type: 'boolean',
    sortable: true,
    visibleInQuickView: false,
    filterOptions: [
      { name: 'Yes', value: true },
      { name: 'No', value: false },
    ],
  })
  isActive: boolean;

  entreePrice?: number;
  entreeDeliveryPrice?: number;

  type: string = 'pricing';

  @Column({
    field: 'deliveryUpchargePercentage',
    title: 'Delivery Upcharge %',
    order: 11,
    pipeToken: PercentPipe,
    filterable: true,
    filterType: 'percentage',
    sortable: true,
    width: 90,
    hidden: true,
  })
  deliveryUpchargePercentage?: number;

  entreeOverridden?: boolean;

  isTwoItemCombo?: boolean;

  @Column({
    field: 'entreeParId',
    title: 'Combo Brink ID',
    order: 2.1,
    width: 90,
    filterable: true,
    filterType: 'custom',
    type: 'number',
    sortable: true,
    visibleInQuickView: false,
  })
  entreeParId: number;

  @Column({
    field: 'lastModPricingInstore',
    hidden: true,
    title: 'Last Modified Pricing In-Store',
    order: 12,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  lastModPricingInstore?: Date;

  @Column({
    field: 'lastModPricingDelivery',
    hidden: true,
    title: 'Last Modified Pricing Delivery',
    order: 13,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  lastModPricingDelivery?: Date;

  isDeliveryAvailable?: boolean;

  constructor(changeset?: Partial<KidMealChangeset>) {
    this.id = uuidv4();
    this.comboName = changeset?.comboName || '';
    this.comboParId = changeset?.comboParId || '';
    this.restaurantId = changeset?.restaurantId || '';
    this.restaurantName = changeset?.restaurantName || '';
    this.itemPrice = changeset?.itemPrice || 0;
    this.comboId = changeset?.comboId || '';
    this.deliveryPrice = changeset?.deliveryPrice;
    this.newDeliveryPrice = changeset?.newDeliveryPrice;
    this.newPrice = changeset?.newPrice;
    this.entreeName = changeset?.entreeName || '';
    this.entreePriceAlc = changeset?.entreePriceAlc;
    this.entreePrice = changeset?.entreePrice;
    this.entreeDeliveryPrice = changeset?.entreeDeliveryPrice;
    this.entreeId = changeset?.entreeId || '';
    this.comboMarkup = changeset?.comboMarkup;
    this.comboDiscount = changeset?.comboDiscount;
    this.restaurantId = changeset?.restaurantId || '';
    this.pricingScheduledDate = changeset?.pricingScheduledDate || null;
    this.availabilityScheduledDate =
      changeset?.availabilityScheduledDate || null;
    this.tierName = changeset?.tierName || '';
    this.isActive = changeset?.isActive || false;
    this.deliveryUpchargePercentage = changeset?.deliveryUpchargePercentage;
    this.entreeOverridden = changeset?.entreeOverridden || false;
    this.entreeParId = changeset?.entreeParId || 0;
    this.toyPrice = changeset?.toyPrice || 0;
    this.status = changeset?.status;
    this.lastModPricingDelivery = changeset?.lastModPricingDelivery;
    this.lastModPricingInstore = changeset?.lastModPricingInstore;

    this.isDeliveryAvailable = changeset?.isDeliveryAvailable;
  }
}

export class GridRowKidMealChangeset extends GridRow(KidMealChangeset) {}